import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import pixService from "@/services/pixService";
import orderService from "@/services/orderService";
import stockService from "@/services/stockService";
import menuService from "@/services/menuService";
import { useAuth } from "@/hooks/useAuth";
import { showInfo, showWarning } from "@/store/errorStore";
import { printOrder } from "@/services/printService";
import { getCompanyName } from "@/lib/company";
import { cn } from "@/lib/utils";
import {
  formatCurrency,
  formatElapsed,
  formatWeekday,
  getDisplayName,
} from "@/lib/format";
import ActiveOrderCard from "@/components/dashboard/ActiveOrderCard";
import {
  PERIODS,
  computeMetrics,
  getStockAlerts,
  sortActiveOrders,
} from "@/components/dashboard/dashboardMetrics";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Separator } from "./ui/separator";
import {
  ArrowUpRight,
  Ban,
  ChevronRight,
  ClipboardCheck,
  Cloud,
  CloudOff,
  Leaf,
  Pause,
  Play,
  ReceiptText,
  RefreshCw,
  TrendingDown,
  TrendingUp,
  Package,
  ShoppingCart,
  Clock,
  CheckCircle,
  Truck,
  Ticket,
  Phone,
  Printer,
  X,
  Eye,
  Wallet,
  ShoppingBag,
  User,
  DollarSign,
  MapPin,
  Copy,
} from "lucide-react";

// Quantos pedidos em andamento aparecem antes do "Mostrar todos"
const VISIBLE_ORDERS = 6;

/** "Tortinha de Limão e Açaí em falta" / "5 produtos com estoque baixo" */
const formatStockAlert = (alerts) => {
  const soldOut = alerts.filter((item) => item.soldOut);
  const list = soldOut.length ? soldOut : alerts;
  const names = list.slice(0, 2).map((item) => item.name);
  const extra = list.length - names.length;
  const joined =
    names.length === 2 ? `${names[0]} e ${names[1]}` : names[0] || "";
  const suffix = extra > 0 ? ` +${extra}` : "";
  return soldOut.length
    ? `${joined}${suffix} esgotado${list.length > 1 ? "s" : ""}`
    : `${joined}${suffix} com estoque baixo`;
};

// Helper function para normalizar endereço
const normalizeAddress = (value) => {
  if (!value) return null;
  if (typeof value === "object") return value;
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch (error) {
      return { description: value };
    }
  }
  return null;
};

// Modal de Detalhes do Pedido
const OrderDetailsModal = ({
  open,
  onOpenChange,
  order,
  onPrintOrder,
  showTextModal,
  setShowTextModal,
  receiptText,
  setReceiptText,
}) => {
  if (!order) return null;

  const calculateOrderTotal = () => {
    let productsTotal = 0;
    if (order.products && Array.isArray(order.products)) {
      order.products.forEach((product) => {
        productsTotal += parseFloat(product.total_value || 0);
      });
    }
    const discountPercentage = parseFloat(order.discount_percentage || 0);
    const discountAmount = productsTotal * (discountPercentage / 100);
    const subtotalWithDiscount = productsTotal - discountAmount;
    const freightValue = parseFloat(order.freight_value || 0);
    return subtotalWithDiscount + freightValue;
  };

  const getProductsSubtotal = () => {
    let total = 0;
    if (order.products && Array.isArray(order.products)) {
      order.products.forEach((product) => {
        total += parseFloat(product.total_value || 0);
      });
    }
    return total;
  };

  const getDiscountAmount = () => {
    const productsTotal = getProductsSubtotal();
    const discountPercentage = parseFloat(order.discount_percentage || 0);
    return productsTotal * (discountPercentage / 100);
  };

  const generateReceiptText = () => {
    const lineWidth = 32;
    const separator = "=".repeat(lineWidth);
    const dashedLine = "-".repeat(lineWidth);

    const removeAccents = (text) => {
      const map = {
        á: "a",
        à: "a",
        ã: "a",
        â: "a",
        ä: "a",
        é: "e",
        è: "e",
        ê: "e",
        ë: "e",
        í: "i",
        ì: "i",
        î: "i",
        ï: "i",
        ó: "o",
        ò: "o",
        õ: "o",
        ô: "o",
        ö: "o",
        ú: "u",
        ù: "u",
        û: "u",
        ü: "u",
        ç: "c",
        ñ: "n",
        Á: "A",
        À: "A",
        Ã: "A",
        Â: "A",
        Ä: "A",
        É: "E",
        È: "E",
        Ê: "E",
        Ë: "E",
        Í: "I",
        Ì: "I",
        Î: "I",
        Ï: "I",
        Ó: "O",
        Ò: "O",
        Õ: "O",
        Ô: "O",
        Ö: "O",
        Ú: "U",
        Ù: "U",
        Û: "U",
        Ü: "U",
        Ç: "C",
        Ñ: "N",
      };
      return text
        .split("")
        .map((char) => map[char] || char)
        .join("");
    };

    const centerText = (text) => {
      const padding = Math.max(0, Math.floor((lineWidth - text.length) / 2));
      return " ".repeat(padding) + text;
    };

    const formatDate = (dateString) => {
      if (!dateString) return new Date().toLocaleString("pt-BR");
      return new Date(dateString).toLocaleString("pt-BR");
    };

    const productsSubtotal = getProductsSubtotal();
    const discountAmount = getDiscountAmount();
    const total = calculateOrderTotal();

    const brand = removeAccents(getCompanyName(order)).toUpperCase();

    let text = "";
    if (brand) text += centerText(brand) + "\n";
    text += centerText("Comanda de Pedido") + "\n";
    text += separator + "\n";
    text += `Pedido #${order.code}\n`;
    text += `Data ${formatDate(order.created_at)}\n`;
    text += `Status ${removeAccents(order.status || "N/A")}\n`;
    text += separator + "\n";

    text += "\nCLIENTE\n";
    const clientName = removeAccents(order.client?.name || "Anonimo");
    text += clientName + "\n";
    text += `Tel ${order.client?.contact || "N/A"}\n`;
    text += separator + "\n";

    text += "\nITENS DO PEDIDO\n";
    text += dashedLine + "\n";

    order.products?.forEach((product, idx) => {
      const prodName = removeAccents(product.product?.name || "Produto");
      text += `\n${idx + 1}. ${prodName} | Qtd ${product.quantity}\n`;
      text += `   Tot R$ ${parseFloat(product.total_value).toFixed(2)}\n`;

      if (product.observation) {
        text += `   Obs: ${removeAccents(product.observation)}\n`;
      }
      text += dashedLine + "\n";
    });

    text += "\n" + separator + "\n";
    text += `Subtotal R$ ${productsSubtotal.toFixed(2)}\n`;

    if (order.coupon_code) {
      text += `Desconto ${order.coupon_code}\n`;
      text += `${parseFloat(order.discount_percentage || 0).toFixed(
        1
      )}% -R$ ${discountAmount.toFixed(2)}\n`;
    }

    text += separator + "\n";
    text += `TOTAL R$ ${total.toFixed(2)}\n`;
    text += separator + "\n";
    text += `\nPagamento ${removeAccents(order.pay_method || "N/A")}\n`;
    text += `Entrega ${removeAccents(order.delivery_type || "N/A")}\n`;
    if (order.payed) {
      text += "Status PAGO\n";
    }
    text += separator + "\n";
    text += "\n" + centerText("Obrigado!") + "\n";
    if (brand) text += centerText(brand) + "\n";
    text += "\n";

    return text;
  };

  const handleCopyText = () => {
    const text = generateReceiptText();
    setReceiptText(text);
    setShowTextModal(true);
  };

  const copyToClipboard = async () => {
    try {
      const removeAccents = (text) => {
        const map = {
          á: "a",
          à: "a",
          ã: "a",
          â: "a",
          ä: "a",
          é: "e",
          è: "e",
          ê: "e",
          ë: "e",
          í: "i",
          ì: "i",
          î: "i",
          ï: "i",
          ó: "o",
          ò: "o",
          õ: "o",
          ô: "o",
          ö: "o",
          ú: "u",
          ù: "u",
          û: "u",
          ü: "u",
          ç: "c",
          ñ: "n",
          Á: "A",
          À: "A",
          Ã: "A",
          Â: "A",
          Ä: "A",
          É: "E",
          È: "E",
          Ê: "E",
          Ë: "E",
          Í: "I",
          Ì: "I",
          Î: "I",
          Ï: "I",
          Ó: "O",
          Ò: "O",
          Õ: "O",
          Ô: "O",
          Ö: "O",
          Ú: "U",
          Ù: "U",
          Û: "U",
          Ü: "U",
          Ç: "C",
          Ñ: "N",
        };
        return text
          .split("")
          .map((char) => map[char] || char)
          .join("");
      };

      const textWithoutAccents = removeAccents(receiptText);

      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(textWithoutAccents);
        showInfo("Texto copiado para a área de transferência!", "Copiado");
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = textWithoutAccents;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const successful = document.execCommand("copy");
        textArea.remove();
        if (successful) {
          showInfo("Texto copiado para a área de transferência!", "Copiado");
        } else {
          throw new Error("execCommand falhou");
        }
      }
    } catch (err) {
      console.error("Erro ao copiar:", err);
      showWarning("Não foi possível copiar automaticamente.");
    }
  };

  const hasFreight =
    order.delivery_type?.toLowerCase() === "delivery" &&
    parseFloat(order.freight_value || 0) > 0;

  const isDelivery = order.delivery_type?.toLowerCase() === "delivery";

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg md:text-xl">
              <ShoppingBag className="h-5 w-5 md:h-6 md:w-6 text-[#1b5e20]" />
              Pedido #{order.code}
              {order.payed === true && (
                <Badge className="text-xs md:text-sm bg-green-600 text-white border-0 shadow-sm ml-2">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Pago
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs md:text-sm mt-2 px-6">
            <div className="flex items-center gap-1">
              <User className="h-3 w-3" />
              <strong>{order.client?.name || "N/A"}</strong>
            </div>
            <div className="flex items-center gap-1">
              <Phone className="h-3 w-3" />
              <strong>{order.client?.contact || "N/A"}</strong>
            </div>
            <div className="flex items-center gap-1">
              <DollarSign className="h-3 w-3" />
              <strong>{order.pay_method || "N/A"}</strong>
            </div>
            <div className="flex items-center gap-1">
              <Package className="h-3 w-3" />
              <strong>{order.delivery_type || "N/A"}</strong>
            </div>
          </div>

          <Separator className="my-4" />

          <div className="space-y-4">
            {/* Produtos do Pedido */}
            {order.products && order.products.length > 0 && (
              <div className="space-y-3">
                <h3 className="font-bold text-base md:text-lg flex items-center gap-2">
                  <ShoppingBag className="h-5 w-5" />
                  Itens do Pedido
                </h3>
                {order.products.map((product, productIdx) => (
                  <div
                    key={productIdx}
                    className="border rounded-lg p-3 md:p-4 bg-gray-50 space-y-3"
                  >
                    <div className="flex justify-between items-start gap-3">
                      <div className="flex-1 space-y-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant="outline" className="text-xs">
                            #{productIdx + 1}
                          </Badge>
                          <Badge variant="secondary" className="text-xs">
                            Qtd: {product.quantity}
                          </Badge>
                        </div>
                        {product.product && (
                          <div>
                            <p className="font-bold text-gray-900 text-sm md:text-base">
                              {product.product.name}
                            </p>
                            {product.product.description && (
                              <p className="text-xs text-gray-600">
                                {product.product.description}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                      <Badge className="bg-[#1b5e20] text-white whitespace-nowrap text-xs md:text-sm">
                        R$ {parseFloat(product.total_value).toFixed(2)}
                      </Badge>
                    </div>

                    {product.observation && (
                      <div className="bg-yellow-50 p-2 md:p-3 rounded border border-yellow-200">
                        <p className="text-xs font-semibold text-yellow-800 mb-1">
                          Observação:
                        </p>
                        <p className="text-xs md:text-sm text-yellow-900">
                          {product.observation}
                        </p>
                      </div>
                    )}

                    {product.selected_produtos &&
                      product.selected_produtos.length > 0 && (
                        <div className="bg-blue-50 p-2 md:p-3 rounded border border-blue-200 space-y-1">
                          <p className="text-xs md:text-sm font-semibold text-blue-900">
                            Produtos Selecionados
                          </p>
                          {product.selected_produtos.map((prod, idx) => (
                            <div
                              key={idx}
                              className="flex justify-between items-center text-xs md:text-sm pl-3 pb-1 border-b border-blue-100 last:border-b-0"
                            >
                              <span className="text-blue-900">
                                {prod.name}{" "}
                                <span className="text-gray-500">
                                  x{prod.qty}
                                </span>
                              </span>
                              <span className="font-semibold text-blue-900">
                                R$ {(prod.price * prod.qty).toFixed(2)}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}

                    {product.selected_ingredients &&
                      product.selected_ingredients.length > 0 && (
                        <div className="bg-amber-50 p-2 md:p-3 rounded border border-amber-200 space-y-1">
                          <p className="text-xs md:text-sm font-semibold text-amber-900">
                            Ingredientes Inclusos
                          </p>
                          <div className="pl-3 space-y-1">
                            {product.selected_ingredients.map((ing, idx) => (
                              <div
                                key={idx}
                                className="flex justify-between text-xs md:text-sm text-amber-900"
                              >
                                <span>• {ing.name}</span>
                                <span className="font-semibold">
                                  +R$ {(ing.price || 0).toFixed(2)}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                    {product.selectedExtraIngredients &&
                      product.selectedExtraIngredients.length > 0 && (
                        <div className="bg-green-50 p-2 md:p-3 rounded border border-green-200 space-y-1">
                          <p className="text-xs md:text-sm font-semibold text-green-900">
                            Ingredientes Extras
                          </p>
                          <div className="pl-3 space-y-1">
                            {product.selectedExtraIngredients.map(
                              (ing, idx) => (
                                <div
                                  key={idx}
                                  className="flex justify-between text-xs md:text-sm text-green-900"
                                >
                                  <span>• {ing.name}</span>
                                  <span className="font-semibold">
                                    +R$ {(ing.price || 0).toFixed(2)}
                                  </span>
                                </div>
                              )
                            )}
                          </div>
                        </div>
                      )}

                    {product.selectedExtraProdutos &&
                      product.selectedExtraProdutos.length > 0 && (
                        <div className="bg-purple-50 p-2 md:p-3 rounded border border-purple-200 space-y-1">
                          <p className="text-xs md:text-sm font-semibold text-purple-900">
                            Produtos Extras
                          </p>
                          {product.selectedExtraProdutos.map((prod, idx) => (
                            <div
                              key={idx}
                              className="flex justify-between items-center text-xs md:text-sm pl-3 pb-1 border-b border-purple-100 last:border-b-0"
                            >
                              <span className="text-purple-900">
                                {prod.name}{" "}
                                <span className="text-gray-500">
                                  x{prod.qty}
                                </span>
                              </span>
                              <span className="font-semibold text-purple-900">
                                R$ {(prod.price * prod.qty).toFixed(2)}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                  </div>
                ))}
              </div>
            )}

            {/* Total Geral */}
            <Separator />

            {hasFreight && (
              <div className="bg-gradient-to-r from-orange-50 to-amber-50 p-4 rounded-lg border-l-4 border-orange-500 shadow-sm">
                <div className="flex items-start gap-3">
                  <DollarSign className="h-5 w-5 text-orange-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-bold text-orange-800 mb-2">
                      Taxa de Entrega Incluída
                    </p>
                    <div className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="text-orange-700">
                          Subtotal produtos:
                        </span>
                        <span className="font-semibold text-orange-900">
                          R$ {getProductsSubtotal().toFixed(2)}
                        </span>
                      </div>
                      {order.coupon_code && (
                        <div className="flex justify-between text-sm items-center">
                          <div className="flex items-center gap-2">
                            <span className="text-purple-700">Desconto:</span>
                            <Badge className="text-xs bg-purple-600 text-white border-0">
                              <Ticket className="h-3 w-3 mr-1" />
                              {order.coupon_code} (
                              {parseFloat(
                                order.discount_percentage || 0
                              ).toFixed(2)}
                              %)
                            </Badge>
                          </div>
                          <span className="font-semibold text-purple-700">
                            - R$ {getDiscountAmount().toFixed(2)}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between text-sm">
                        <span className="text-orange-700">
                          Taxa de delivery:
                        </span>
                        <span className="font-bold text-orange-900">
                          R$ {parseFloat(order.freight_value || 0).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-between items-center bg-gradient-to-r from-green-50 to-emerald-50 p-3 md:p-4 rounded-lg border-2 border-green-300">
              <span className="text-sm md:text-lg font-bold text-gray-900 flex items-center gap-2">
                <DollarSign className="h-4 w-4 md:h-5 md:w-5 text-green-600" />
                Total {hasFreight ? "(com taxa)" : ""}:
              </span>
              <span className="text-xl md:text-3xl font-bold text-green-600">
                R$ {calculateOrderTotal().toFixed(2)}
              </span>
            </div>

            {/* Botões de Impressão */}
            <div className="grid grid-cols-2 gap-3">
              <Button
                onClick={() => onPrintOrder && onPrintOrder(order)}
                className="w-full bg-gradient-to-r from-[#1b5e20] to-[#144919] hover:from-[#144919] hover:to-[#0e3a12] text-white flex items-center justify-center gap-2"
              >
                <Printer className="h-4 w-4" />
                Imprimir
              </Button>
              <Button
                onClick={handleCopyText}
                className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white flex items-center justify-center gap-2"
              >
                <Copy className="h-4 w-4" />
                Copiar Texto
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Texto para Copiar */}
      <Dialog open={showTextModal} onOpenChange={setShowTextModal}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Copy className="h-5 w-5" />
              Texto do Pedido #{order.code}
            </DialogTitle>
            <DialogDescription>
              Copie o texto abaixo e cole no software de impressão térmica
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
              <p className="text-blue-800 font-medium mb-1">
                ℹ️ Texto otimizado para impressora térmica
              </p>
              <p className="text-blue-700 text-xs">
                O texto está sem acentos para garantir compatibilidade com
                impressoras térmicas.
              </p>
            </div>
            <textarea
              value={receiptText}
              readOnly
              className="w-full h-96 p-4 font-mono text-xs border rounded-lg bg-gray-50 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
              style={{
                fontFamily: "'Courier New', monospace",
                whiteSpace: "pre",
              }}
            />
            <div className="flex gap-3">
              <Button
                onClick={copyToClipboard}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
              >
                <Copy className="h-4 w-4 mr-2" />
                Copiar para Área de Transferência
              </Button>
              <Button
                onClick={() => setShowTextModal(false)}
                variant="outline"
                className="flex-1"
              >
                Fechar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Modal de Confirmação de Pagamento
const PaymentConfirmationModal = ({
  open,
  onOpenChange,
  order,
  onConfirm,
  loading,
}) => {
  const [paymentData, setPaymentData] = useState({
    pay_method: "",
    value_received: "",
    name: "",
    contact: "",
  });

  const paymentMethods = [
    "PIX",
    "Dinheiro",
    "Cartão de Crédito",
    "Cartão de Débito",
    "Vale Refeição",
    "Transferência Bancária",
  ];

  useEffect(() => {
    if (open && order) {
      const productsTotal =
        order.products?.reduce(
          (sum, product) => sum + parseFloat(product.total_value || 0),
          0
        ) || 0;
      const freightValue = parseFloat(order.freight_value || 0);
      const discountPercentage = parseFloat(order.discount_percentage || 0);
      const discountAmount = productsTotal * (discountPercentage / 100);
      const orderTotal = productsTotal - discountAmount + freightValue;

      setPaymentData({
        pay_method: order.pay_method || "",
        value_received: orderTotal.toFixed(2),
        name: order.client?.name || "",
        contact: order.client?.contact || "",
      });
    }
  }, [open, order]);

  const handleConfirm = () => {
    if (!paymentData.pay_method || !paymentData.value_received) {
      showWarning("Preencha todos os campos obrigatórios");
      return;
    }

    onConfirm({
      ...paymentData,
      amount: parseFloat(paymentData.value_received),
      value_received: parseFloat(paymentData.value_received),
      orderCode: order.code,
    });
  };

  if (!order) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Wallet className="h-5 w-5 text-[#1b5e20]" />
            Confirmar Pagamento
          </DialogTitle>
          <DialogDescription className="text-sm">
            Pedido <strong>#{order.code}</strong> - {order.client?.name}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Método de Pagamento */}
          <div className="space-y-2">
            <Label
              htmlFor="payment-method"
              className="text-sm font-semibold text-gray-700"
            >
              Método de Pagamento <span className="text-red-500">*</span>
            </Label>
            <Select
              value={paymentData.pay_method}
              onValueChange={(value) =>
                setPaymentData({ ...paymentData, pay_method: value })
              }
            >
              <SelectTrigger id="payment-method" className="h-11">
                <SelectValue placeholder="Selecione o método de pagamento" />
              </SelectTrigger>
              <SelectContent>
                {paymentMethods.map((method) => (
                  <SelectItem key={method} value={method}>
                    {method}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Valor Recebido */}
          <div className="space-y-2">
            <Label
              htmlFor="value-received"
              className="text-sm font-semibold text-gray-700"
            >
              Valor Recebido (R$) <span className="text-red-500">*</span>
            </Label>
            <Input
              id="value-received"
              type="number"
              step="0.01"
              min="0"
              value={paymentData.value_received}
              onChange={(e) =>
                setPaymentData({
                  ...paymentData,
                  value_received: e.target.value,
                })
              }
              className="h-11"
              placeholder="0.00"
            />
          </div>

          {/* Nome do Cliente */}
          <div className="space-y-2">
            <Label
              htmlFor="client-name"
              className="text-sm font-semibold text-gray-700"
            >
              Nome do Cliente
            </Label>
            <Input
              id="client-name"
              type="text"
              value={paymentData.name}
              onChange={(e) =>
                setPaymentData({ ...paymentData, name: e.target.value })
              }
              className="h-11"
              placeholder="Nome do cliente"
            />
          </div>

          {/* Contato */}
          <div className="space-y-2">
            <Label
              htmlFor="contact"
              className="text-sm font-semibold text-gray-700"
            >
              Contato
            </Label>
            <Input
              id="contact"
              type="text"
              value={paymentData.contact}
              onChange={(e) =>
                setPaymentData({ ...paymentData, contact: e.target.value })
              }
              className="h-11"
              placeholder="Telefone ou e-mail"
            />
          </div>

          {/* Resumo */}
          <div className="bg-gray-50 p-4 rounded-lg border space-y-2">
            <p className="text-xs font-semibold text-gray-600 uppercase">
              Resumo
            </p>
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Pedido:</span>
                <span className="font-semibold">#{order.code}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Pagamento:</span>
                <span className="font-semibold">
                  {paymentData.pay_method || "-"}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Valor:</span>
                <span className="font-bold text-[#1b5e20]">
                  R$ {paymentData.value_received || "0.00"}
                </span>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
            className="flex-1"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={
              loading || !paymentData.pay_method || !paymentData.value_received
            }
            className="flex-1 bg-[#1b5e20] hover:bg-[#144919]"
          >
            {loading ? "Confirmando..." : "Confirmar Pagamento"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const AUTO_REFRESH_MS = 60000;

// Atalhos da home: primeiros menus liberados, nesta ordem de preferência
const SHORTCUT_LINKS = [
  "orders",
  "products",
  "stocks",
  "pix",
  "categories",
  "coupons",
  "clients",
  "companies",
];

const Dashboard = ({
  menuItems = [],
  canAccess = () => false,
  onNavigate,
  pendingCount = 0,
}) => {
  const { user, company, companyName, setStoreAvailability } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [lastSync, setLastSync] = useState(null);
  const [syncFailed, setSyncFailed] = useState(false);
  const [now, setNow] = useState(() => new Date());
  const [period, setPeriod] = useState("today");
  const [showAllOrders, setShowAllOrders] = useState(false);
  const [updatingCode, setUpdatingCode] = useState(null);
  const [orderToCancel, setOrderToCancel] = useState(null);
  const [stockAlerts, setStockAlerts] = useState([]);
  const [togglingStore, setTogglingStore] = useState(false);
  const [confirmPause, setConfirmPause] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [showTextModal, setShowTextModal] = useState(false);
  const [receiptText, setReceiptText] = useState("");
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedOrderForPayment, setSelectedOrderForPayment] = useState(null);
  const [loading, setLoading] = useState(false);

  const canViewStocks = canAccess("stocks");

  /**
   * Todos os pedidos da empresa: alimentam os indicadores do período e a
   * lista de pedidos em andamento. `silent` = atualização em segundo plano
   * (sem spinner nem modal de erro).
   */
  const fetchOrders = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoadingOrders(true);
    try {
      const allOrders = await orderService.getAdminOrders(
        undefined,
        silent ? { silent: true } : {},
      );
      setOrders(Array.isArray(allOrders) ? allOrders : []);
      setLastSync(new Date());
      setSyncFailed(false);
    } catch (error) {
      console.error("Erro ao buscar pedidos:", error);
      setSyncFailed(true);
    } finally {
      if (!silent) setLoadingOrders(false);
    }
  }, []);

  // Alerta de estoque (só para quem tem a tela de estoque liberada)
  const fetchStockAlerts = useCallback(async () => {
    if (!canViewStocks) {
      setStockAlerts([]);
      return;
    }
    try {
      const stocks = await stockService.getAllStocks({ silent: true });
      setStockAlerts(getStockAlerts(Array.isArray(stocks) ? stocks : []));
    } catch (error) {
      setStockAlerts([]);
    }
  }, [canViewStocks]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  useEffect(() => {
    fetchStockAlerts();
  }, [fetchStockAlerts]);

  // Atualização automática e relógio dos "há X min"
  useEffect(() => {
    const id = setInterval(() => {
      setNow(new Date());
      fetchOrders({ silent: true });
    }, AUTO_REFRESH_MS);
    return () => clearInterval(id);
  }, [fetchOrders]);

  // Chegou/saiu pedido "Aguardando" (WebSocket/polling do AdminPanel)
  const lastPending = useRef(pendingCount);
  useEffect(() => {
    if (lastPending.current !== pendingCount) {
      lastPending.current = pendingCount;
      fetchOrders({ silent: true });
    }
  }, [pendingCount, fetchOrders]);

  const metrics = useMemo(
    () => computeMetrics(orders, period, now),
    [orders, period, now],
  );
  const activeOrders = useMemo(() => sortActiveOrders(orders), [orders]);
  const visibleOrders = showAllOrders
    ? activeOrders
    : activeOrders.slice(0, VISIBLE_ORDERS);
  const periodInfo = PERIODS.find((p) => p.id === period) || PERIODS[0];

  const shortcuts = useMemo(
    () =>
      SHORTCUT_LINKS.map((link) => menuItems.find((item) => item.link === link))
        .filter(Boolean)
        .slice(0, 4),
    [menuItems],
  );

  const navigate = (link) => {
    if (onNavigate && canAccess(link)) onNavigate(link);
  };

  // Atualizar status do pedido
  const handleUpdateStatus = async (orderCode, newStatus) => {
    setUpdatingCode(orderCode);
    try {
      await orderService.updateStatus(orderCode, newStatus);
      setOrders((prevOrders) =>
        prevOrders.map((order) =>
          order.code === orderCode ? { ...order, status: newStatus } : order,
        ),
      );
    } catch (error) {
      // Modal global já exibido pelo interceptor
      console.error("Erro ao atualizar status:", error);
    } finally {
      setUpdatingCode(null);
    }
  };

  const handleConfirmCancel = async () => {
    const order = orderToCancel;
    setOrderToCancel(null);
    if (order) await handleUpdateStatus(order.code, "Cancelado");
  };

  // Abrir/pausar a loja (pausar pede confirmação)
  const handleStoreToggle = async (available) => {
    setConfirmPause(false);
    setTogglingStore(true);
    try {
      await setStoreAvailability(available);
      showInfo(
        available
          ? "Loja aberta: os clientes já podem fazer pedidos."
          : "Loja pausada: novos pedidos estão bloqueados.",
        available ? "Loja Aberta" : "Loja Pausada",
      );
    } catch (error) {
      // Modal global já exibido pelo interceptor
      console.error("Erro ao alterar status da loja:", error);
    } finally {
      setTogglingStore(false);
    }
  };

  // WhatsApp
  const handleWhatsApp = (order) => {
    const phone =
      order.client?.contact || order.client?.phone || order.client_phone || "";
    const cleanPhone = phone.replace(/\D/g, "");
    if (!cleanPhone) {
      showWarning("Telefone não disponível");
      return;
    }
    const message = `Olá! Seu pedido #${
      order.code
    } está ${String(order.status || "").toLowerCase()}.`;
    window.open(
      `https://wa.me/55${cleanPhone}?text=${encodeURIComponent(message)}`,
      "_blank"
    );
  };

  // Visualizar detalhes do pedido
  const handleViewDetails = (order) => {
    setSelectedOrder(order);
    setModalOpen(true);
  };

  // Confirmar pagamento
  const handleConfirmPayment = (order) => {
    setSelectedOrderForPayment(order);
    setPaymentModalOpen(true);
  };

  // Processar confirmação de pagamento
  const handlePaymentConfirm = async (paymentData) => {
    setLoading(true);
    try {
      await pixService.confirmPayment(paymentData.orderCode, paymentData);
      setPaymentModalOpen(false);
      showInfo("Pagamento confirmado com sucesso!", "Pagamento");
      // Recarregar pedidos
      await fetchOrders({ silent: true });
    } catch (error) {
      // Erros HTTP já abrem o modal global; aqui só validação local
      console.error("Erro ao confirmar pagamento:", error);
      if (!error.response && !error.request) {
        showWarning(error.message || "Não foi possível confirmar o pagamento.");
      }
    } finally {
      setLoading(false);
    }
  };

  const displayName = getDisplayName(user);
  const storeOpen = !!company?.available;
  const revenueChange = metrics.revenueChange;
  const paidPercent = Math.round(metrics.paidRatio * 100);

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-4 md:gap-6">
      {/* Saudação, status da loja e período */}
      <section className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 flex-col">
            <h1 className="flex items-center gap-1.5 font-display text-lg font-bold text-foreground md:text-2xl">
              <span className="truncate">
                Olá{displayName ? `, ${displayName}` : ""}
              </span>
              <Leaf className="h-5 w-5 flex-shrink-0 text-brand-pistachio" aria-hidden="true" />
            </h1>
            <span className="text-xs text-muted-foreground md:text-sm">
              {formatWeekday(now)} • {companyName || "Todas as empresas"}
            </span>
          </div>

          {company && (
            <div className="flex items-center gap-1.5 rounded-full border bg-card py-1 pl-2.5 pr-1 shadow-soft">
              <span
                className={cn(
                  "h-2.5 w-2.5 rounded-full",
                  storeOpen ? "animate-pulse bg-primary" : "bg-terra",
                )}
              />
              <span
                className={cn(
                  "mr-1 text-[11px] font-bold",
                  storeOpen ? "text-primary" : "text-terra",
                )}
              >
                {storeOpen ? "Aberta" : "Fechada"}
              </span>
              <button
                type="button"
                disabled={togglingStore}
                onClick={() =>
                  storeOpen ? setConfirmPause(true) : handleStoreToggle(true)
                }
                className="flex items-center gap-1 rounded-full border bg-surface px-2.5 py-1 text-[11px] font-semibold text-muted-foreground transition-all hover:bg-muted active:scale-95 disabled:opacity-60"
                title={storeOpen ? "Pausar novos pedidos" : "Abrir a loja"}
              >
                {storeOpen ? (
                  <Pause className="h-3.5 w-3.5 text-primary" />
                ) : (
                  <Play className="h-3.5 w-3.5 text-primary" />
                )}
                {togglingStore ? "Salvando..." : storeOpen ? "Pausar" : "Abrir"}
              </button>
            </div>
          )}
        </div>

        <div
          className="no-scrollbar flex items-center gap-1.5 overflow-x-auto py-0.5"
          role="tablist"
          aria-label="Período dos indicadores"
        >
          {PERIODS.map((item) => (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={period === item.id}
              onClick={() => setPeriod(item.id)}
              className={cn(
                "whitespace-nowrap rounded-full px-3.5 py-1 text-xs font-semibold transition-colors",
                period === item.id
                  ? "bg-primary font-bold text-primary-foreground shadow-sm"
                  : "border bg-card text-muted-foreground hover:bg-muted",
              )}
            >
              {item.label}
            </button>
          ))}
        </div>
      </section>

      {/* Indicadores do período */}
      <section className="grid grid-cols-2 gap-2 md:grid-cols-4 md:gap-3">
        <div className="flex flex-col justify-between rounded-xl border bg-card p-3 shadow-soft md:p-4">
          <div className="flex items-center justify-between gap-1">
            <span className="text-[11px] font-semibold text-muted-foreground md:text-xs">
              Faturamento
            </span>
            {revenueChange !== null && (
              <span
                className={cn(
                  "flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-bold",
                  revenueChange >= 0
                    ? "bg-secondary text-primary"
                    : "bg-terra-soft text-terra",
                )}
                title={`Comparado com ${periodInfo.compareLabel} (${formatCurrency(metrics.previousRevenue)})`}
              >
                {revenueChange >= 0 ? (
                  <TrendingUp className="h-3 w-3" />
                ) : (
                  <TrendingDown className="h-3 w-3" />
                )}
                {revenueChange >= 0 ? "+" : ""}
                {Math.round(revenueChange)}%
              </span>
            )}
          </div>
          <div className="my-1.5">
            <span className="block font-display text-lg font-bold leading-tight text-primary min-[380px]:text-xl md:text-2xl">
              {loadingOrders ? "—" : formatCurrency(metrics.revenue)}
            </span>
            <div
              className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-surface-high"
              role="progressbar"
              aria-label="Valor já recebido"
              aria-valuenow={paidPercent}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <div
                className="h-full rounded-full bg-primary transition-all"
                style={{ width: `${paidPercent}%` }}
              />
            </div>
          </div>
          <span className="truncate text-[11px] text-muted-foreground">
            {metrics.revenue > 0
              ? `${paidPercent}% recebido (${formatCurrency(metrics.paid)})`
              : "Nenhuma venda no período"}
          </span>
        </div>

        <div className="flex flex-col justify-between rounded-xl border bg-card p-3 shadow-soft md:p-4">
          <div className="flex items-center justify-between gap-1">
            <span className="text-[11px] font-semibold text-muted-foreground md:text-xs">
              {periodInfo.ordersLabel}
            </span>
            <ReceiptText className="h-[18px] w-[18px] text-primary" />
          </div>
          <div className="my-1.5">
            <span className="font-display text-[22px] font-bold leading-tight text-foreground md:text-2xl">
              {loadingOrders ? "—" : metrics.count}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-x-1.5 text-[11px]">
            <span className="font-bold text-primary">{metrics.completed}</span>
            <span className="text-muted-foreground">entregues</span>
            <span className="text-border">•</span>
            <span className="font-bold text-terra">{metrics.inProgress}</span>
            <span className="text-muted-foreground">ativos</span>
          </div>
        </div>

        <div className="hidden flex-col justify-between rounded-xl border bg-card p-4 shadow-soft md:flex">
          <div className="flex items-center justify-between gap-1">
            <span className="text-xs font-semibold text-muted-foreground">
              Ticket Médio
            </span>
            <Wallet className="h-[18px] w-[18px] text-primary" />
          </div>
          <span className="my-1.5 font-display text-2xl font-bold leading-tight text-foreground">
            {loadingOrders ? "—" : formatCurrency(metrics.averageTicket)}
          </span>
          <span className="text-[11px] text-muted-foreground">
            por pedido não cancelado
          </span>
        </div>

        <div className="hidden flex-col justify-between rounded-xl border bg-card p-4 shadow-soft md:flex">
          <div className="flex items-center justify-between gap-1">
            <span className="text-xs font-semibold text-muted-foreground">
              Cancelados
            </span>
            <Ban className="h-[18px] w-[18px] text-terra" />
          </div>
          <span className="my-1.5 font-display text-2xl font-bold leading-tight text-foreground">
            {loadingOrders ? "—" : metrics.cancelled}
          </span>
          <span className="text-[11px] text-muted-foreground">
            pedidos no período
          </span>
        </div>
      </section>

      {/* Alerta operacional de estoque */}
      {stockAlerts.length > 0 && (
        <button
          type="button"
          onClick={() => navigate("stocks")}
          className="group flex w-full items-center justify-between rounded-xl border bg-card p-2.5 text-left shadow-soft transition-colors hover:bg-surface"
        >
          <span className="flex min-w-0 items-center gap-2">
            <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-surface text-primary">
              <ClipboardCheck className="h-[18px] w-[18px]" />
            </span>
            <span className="truncate text-[13px]">
              <span className="font-semibold text-foreground">Estoque:</span>
              <span className="ml-1 text-muted-foreground">
                {formatStockAlert(stockAlerts)}
              </span>
            </span>
          </span>
          <ChevronRight className="ml-1 h-[18px] w-[18px] flex-shrink-0 text-primary transition-transform group-hover:translate-x-0.5" />
        </button>
      )}

      {/* Pedidos em andamento */}
      <section className="flex flex-col gap-2">
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <h2 className="whitespace-nowrap font-display text-[15px] font-bold text-foreground min-[380px]:text-base md:text-lg">
              Pedidos em Andamento
            </h2>
            <span className="whitespace-nowrap rounded-full border border-primary/20 bg-secondary px-2 py-0.5 text-[11px] font-bold text-primary">
              {activeOrders.length}
              <span className="hidden min-[380px]:inline">
                {" "}ativo{activeOrders.length !== 1 ? "s" : ""}
              </span>
            </span>
          </div>
          {canAccess("orders") && (
            <button
              type="button"
              onClick={() => navigate("orders")}
              className="flex flex-shrink-0 items-center gap-0.5 whitespace-nowrap text-xs font-bold text-primary hover:underline"
            >
              Ver todos
              <ArrowUpRight className="h-4 w-4" />
            </button>
          )}
        </div>

        {loadingOrders ? (
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2 md:gap-3 xl:grid-cols-3">
            {Array.from({ length: 2 }, (_, i) => (
              <div key={i} className="h-36 animate-pulse rounded-xl border bg-card" />
            ))}
          </div>
        ) : activeOrders.length === 0 ? (
          <div className="flex flex-col items-center rounded-xl border bg-card px-4 py-8 text-center shadow-soft">
            <ShoppingCart className="mb-3 h-10 w-10 text-muted-foreground/60" />
            <p className="text-sm font-semibold text-foreground">
              Nenhum pedido em andamento
            </p>
            <p className="text-xs text-muted-foreground">
              Os novos pedidos aparecem aqui automaticamente.
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-2 md:grid-cols-2 md:gap-3 xl:grid-cols-3">
              {visibleOrders.map((order) => (
                <ActiveOrderCard
                  key={order.code}
                  order={order}
                  now={now}
                  busy={updatingCode === order.code}
                  onChangeStatus={(o, status) => handleUpdateStatus(o.code, status)}
                  onCancel={setOrderToCancel}
                  onPrint={printOrder}
                  onWhatsApp={handleWhatsApp}
                  onDetails={handleViewDetails}
                  onPayment={handleConfirmPayment}
                />
              ))}
            </div>
            {activeOrders.length > VISIBLE_ORDERS && (
              <button
                type="button"
                onClick={() => setShowAllOrders((value) => !value)}
                className="self-center rounded-full border bg-card px-4 py-1.5 text-xs font-semibold text-primary transition-colors hover:bg-muted"
              >
                {showAllOrders
                  ? "Mostrar menos"
                  : `Mostrar todos (${activeOrders.length})`}
              </button>
            )}
          </>
        )}
      </section>

      {/* Atalhos rápidos (menus liberados para o usuário) */}
      {shortcuts.length > 0 && (
        <section className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-base font-bold text-foreground md:text-lg">
              Atalhos Rápidos
            </h2>
            <span className="text-xs text-muted-foreground">Operação</span>
          </div>
          <div className="grid grid-cols-4 gap-2 md:gap-3">
            {shortcuts.map((item, index) => {
              const Icon = menuService.resolveIcon(item.icon);
              return (
                <button
                  key={item.link}
                  type="button"
                  onClick={() => navigate(item.link)}
                  className="flex flex-col items-center justify-center gap-1.5 rounded-xl border bg-card p-2.5 text-center shadow-soft transition-all hover:bg-surface active:scale-95 md:p-4"
                >
                  <span
                    className={cn(
                      "flex h-9 w-9 items-center justify-center rounded-lg text-primary md:h-10 md:w-10",
                      index === 0 ? "bg-secondary" : "border bg-surface",
                    )}
                  >
                    <Icon className="h-5 w-5" />
                  </span>
                  <span className="line-clamp-2 text-[11px] font-bold leading-tight text-foreground md:text-xs">
                    {item.label}
                  </span>
                </button>
              );
            })}
          </div>
        </section>
      )}

      {/* Sincronismo */}
      <footer className="flex items-center justify-between gap-2 rounded-xl border bg-surface px-3 py-2 text-xs text-muted-foreground">
        <button
          type="button"
          onClick={() => {
            setNow(new Date());
            fetchOrders({ silent: true });
            fetchStockAlerts();
          }}
          className="flex min-w-0 items-center gap-1.5 hover:text-foreground"
          title="Atualizar agora"
        >
          {syncFailed ? (
            <CloudOff className="h-4 w-4 flex-shrink-0 text-terra" />
          ) : (
            <Cloud className="h-4 w-4 flex-shrink-0 text-primary" />
          )}
          <span className="truncate">
            {syncFailed
              ? "Falha ao sincronizar — tocar para tentar de novo"
              : lastSync
                ? `Sincronizado ${formatElapsed(lastSync, now)}`
                : "Sincronizando..."}
          </span>
          <RefreshCw className="h-3.5 w-3.5 flex-shrink-0" />
        </button>
        <span className="flex-shrink-0 text-[11px] font-semibold text-primary">
          v{process.env.REACT_APP_VERSION || "1.0.0"} Bonfim
        </span>
      </footer>

      {/* Confirmação: pausar a loja */}
      <AlertDialog open={confirmPause} onOpenChange={setConfirmPause}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Pausar a loja?</AlertDialogTitle>
            <AlertDialogDescription>
              Enquanto a loja estiver fechada, os clientes não conseguem fazer
              novos pedidos. Os pedidos em andamento não são afetados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Voltar</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleStoreToggle(false)}>
              Pausar loja
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirmação: cancelar pedido */}
      <AlertDialog
        open={!!orderToCancel}
        onOpenChange={(open) => !open && setOrderToCancel(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Cancelar o pedido #{orderToCancel?.code}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              O pedido de {orderToCancel?.client?.name || "cliente não informado"}{" "}
              sai da lista de pedidos em andamento. Esta ação não pode ser
              desfeita por aqui.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Voltar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmCancel}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Cancelar pedido
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modal de Detalhes do Pedido */}
      {selectedOrder && (
        <OrderDetailsModal
          open={modalOpen}
          onOpenChange={setModalOpen}
          order={selectedOrder}
          onPrintOrder={printOrder}
          showTextModal={showTextModal}
          setShowTextModal={setShowTextModal}
          receiptText={receiptText}
          setReceiptText={setReceiptText}
        />
      )}

      {/* Modal de Pagamento */}
      {selectedOrderForPayment && (
        <PaymentConfirmationModal
          open={paymentModalOpen}
          onOpenChange={setPaymentModalOpen}
          order={selectedOrderForPayment}
          onConfirm={handlePaymentConfirm}
          loading={loading}
        />
      )}
    </div>
  );
};

export default Dashboard;
